/* eslint-disable react/no-danger, jsx-a11y/label-has-associated-control */
import Select from '@modules/commons/Select';
import Link from 'next/link';
import Button from '@material-ui/core/Button';
import InputBase from '@material-ui/core/InputBase';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@material-ui/icons/VisibilityOffOutlined';
import { SECONDARY, ERROR } from '@src_theme/colors';
import { useState } from 'react';
import VirtualKeyboard from '@root/src/modules/commons/VirtualKeyboard';
import isUseVirtualKeyboard from '@root/src/helpers/virtualKeyboard';

const BG_PAGE = '#EAEAF4';
const BORDER = '#E5E7EB';
const INPUT_DISABLED_BG = '#F3F4F6';
const TEXT_LABEL = '#1F2937';
const TEXT_INPUT = '#1E242D';
const TEXT_PLACEHOLDER = '#9CA3AF';
const ICON = '#6B7280';

const inputPill = {
    width: '100%',
    background: '#FFFFFF',
    border: `1px solid ${BORDER}`,
    borderRadius: 100,
    padding: '12px 16px',
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    lineHeight: '20px',
    color: TEXT_INPUT,
    transition: 'border-color .2s',
};

const useStyles = makeStyles(() => ({
    root: {
        width: '100%',
        minHeight: '100vh',
        background: BG_PAGE,
        padding: 40,
        display: 'flex',
        justifyContent: 'center',
        fontFamily: 'Inter, sans-serif',
        '@media (max-width: 767px)': {
            padding: 16,
        },
        // tablet ke atas: pas satu layar, tanpa scroll
        '@media (min-width: 768px)': {
            height: '100vh',
        },
        '@media (min-width: 768px) and (max-height: 1000px)': {
            padding: '24px 40px',
        },
    },
    card: {
        width: '100%',
        maxWidth: 944,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
    },
    banner: {
        // banner yang menyusut kalau layar pendek, form tetap utuh
        flex: '0 1 39vh',
        minHeight: 160,
        maxHeight: 500,
        padding: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '20px 20px 0 0',
        backgroundImage: 'url(/assets/img/login/login-banner.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        '& img': {
            width: 375,
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
        },
    },
    formSection: {
        flex: '1 0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '40px',
        padding: '60px 40px',
        background: '#FFFFFF',
        borderRadius: '0 0 20px 20px',
        '@media (max-width: 767px)': {
            padding: '32px 20px',
            gap: '24px',
        },
        '@media (min-width: 768px) and (max-height: 1000px)': {
            padding: '40px',
            gap: '24px',
        },
    },
    title: {
        margin: 0,
        fontFamily: 'Inter, sans-serif',
        fontSize: 30,
        lineHeight: '36px',
        fontWeight: 700,
        color: SECONDARY,
        textAlign: 'center',
    },
    form: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
    },
    fields: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    field: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    labelRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    label: {
        fontSize: 14,
        lineHeight: '20px',
        color: TEXT_LABEL,
    },
    forgotPasswordLink: {
        fontSize: 14,
        lineHeight: '20px',
        fontWeight: 500,
        color: SECONDARY,
        textDecoration: 'none',
    },
    input: {
        ...inputPill,
        '&.Mui-focused': {
            borderColor: SECONDARY,
        },
        '&.Mui-error': {
            borderColor: ERROR,
        },
        '& .MuiInputBase-input': {
            padding: 0,
            height: 20,
            '&::placeholder': {
                color: TEXT_PLACEHOLDER,
                opacity: 1,
            },
        },
    },
    eyeButton: {
        padding: 0,
        marginLeft: 8,
        color: ICON,
        '& svg': {
            fontSize: 20,
        },
    },
    errorText: {
        margin: 0,
        fontSize: 12,
        lineHeight: '16px',
        color: ERROR,
    },
    selectInput: {
        margin: 0,
        '& .MuiInputBase-root': {
            ...inputPill,
            background: INPUT_DISABLED_BG,
            margin: 0,
        },
        '& .MuiInputBase-input': {
            padding: 0,
            height: 20,
            color: TEXT_INPUT,
        },
        '& .MuiSelect-select:focus': {
            background: 'transparent',
        },
        '& .MuiSelect-icon': {
            right: 16,
            color: ICON,
        },
        '& .MuiInput-underline:before, & .MuiInput-underline:after': {
            display: 'none',
        },
    },
    submitBtn: {
        width: '100%',
        padding: '12px 20px',
        borderRadius: 60,
        background: SECONDARY,
        color: '#FFFFFF',
        fontFamily: 'Inter, sans-serif',
        fontSize: 14,
        lineHeight: '20px',
        fontWeight: 600,
        textTransform: 'none',
        boxShadow: 'none',
        '&:hover': {
            background: SECONDARY,
            opacity: 0.9,
            boxShadow: 'none',
        },
    },
    footerHelp: {
        fontSize: 12,
        lineHeight: '16px',
        color: ICON,
        textAlign: 'center',
        '& p': {
            margin: 0,
        },
    },
}));

const FieldError = ({ formik, name }) => {
    const styles = useStyles();
    if (!(formik.touched[name] && formik.errors[name])) return null;
    return <p className={styles.errorText}>{formik.errors[name]}</p>;
};

export const EcatalogLogin = (props) => {
    const styles = useStyles();
    const {
        formik,
        storeData,
    } = props;
    const [showKeyboard, setShowKeyboard] = useState(false);
    const [inputName, setInputName] = useState('default');
    const [showPassword, setShowPassword] = useState(false);

    const handleFocus = (name) => () => {
        if (isUseVirtualKeyboard()) {
            setShowKeyboard(true);
            setInputName(name);
        }
    };

    return (
        <>
            <h1 className={styles.title}>Masuk ke Akun</h1>
            <form className={styles.form} onSubmit={formik.handleSubmit}>
                <div className={styles.fields}>
                    <div className={styles.field}>
                        <label htmlFor="login-email" className={styles.label}>
                            Email / ID Karyawan
                        </label>
                        <InputBase
                            id="login-email"
                            type="email"
                            name="email"
                            placeholder="Masukkan email / ID karyawan"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={!!(formik.touched.email && formik.errors.email)}
                            className={styles.input}
                            onFocus={handleFocus('email')}
                        />
                        <FieldError formik={formik} name="email" />
                    </div>
                    <div className={styles.field}>
                        <div className={styles.labelRow}>
                            <label htmlFor="login-password" className={styles.label}>
                                Password
                            </label>
                            <Link href="/customer/account/forgotpassword">
                                <a className={styles.forgotPasswordLink}>
                                    Lupa Password?
                                </a>
                            </Link>
                        </div>
                        <InputBase
                            id="login-password"
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            placeholder="Masukkan password"
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={!!(formik.touched.password && formik.errors.password)}
                            className={styles.input}
                            onFocus={handleFocus('password')}
                            endAdornment={(
                                <IconButton
                                    className={styles.eyeButton}
                                    aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                                    onClick={() => setShowPassword(!showPassword)}
                                    onMouseDown={(e) => e.preventDefault()}
                                >
                                    {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                                </IconButton>
                            )}
                        />
                        <FieldError formik={formik} name="password" />
                    </div>
                    <div className={styles.field}>
                        <label htmlFor="storeId" className={styles.label}>
                            Lokasi Mitra10
                        </label>
                        <Select
                            showLabel={false}
                            className={styles.selectInput}
                            options={storeData.map((vendor) => ({ value: vendor.vendor_id, label: vendor.vendor_name }))}
                            name="storeId"
                            value={formik.values.storeId}
                            onChange={formik.handleChange}
                            error={!!(formik.touched.storeId && formik.errors.storeId)}
                            errorMessage={(formik.touched.storeId && formik.errors.storeId) || null}
                        />
                    </div>
                </div>
                <Button type="submit" variant="contained" disableElevation className={styles.submitBtn}>
                    Masuk
                </Button>
            </form>
            <VirtualKeyboard
                formik={formik}
                showKeyboard={showKeyboard}
                setShowKeyboard={setShowKeyboard}
                value={formik.values[inputName]}
                inputName={inputName}
            />
        </>
    );
};

export default function LoginContainer({ children, dataCms }) {
    const styles = useStyles();

    return (
        <div className={styles.root}>
            <div className={styles.card}>
                <div className={styles.banner}>
                    <img src="/assets/img/login/ecatalog-logo.png" alt="Mitra10 E-Catalog" />
                </div>
                <div className={styles.formSection}>
                    {children}
                    {dataCms ? <div className={styles.footerHelp} dangerouslySetInnerHTML={{ __html: dataCms }} /> : null}
                </div>
            </div>
        </div>
    );
}
